import React, { useState } from 'react';
import googleSheets from '../../services/googleSheets';
import { useUser } from '../../contexts/UserContext';
import { SecureTokenManager } from '../../utils/secureTokenManager';

interface DashboardHeaderProps {
  onRefresh: () => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ onRefresh }) => {
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<string | null>(null);
  const { user } = useUser();
  
  // Check if current user is admin
  const isAdminUser = (): boolean => {
    const adminEmail = 'amar@mailercloud.com';
    
    // Try UserContext first
    if (user?.email) {
      const isAdmin = user.email === adminEmail;
      if (process.env.REACT_APP_DEBUG) {
        console.log(`Admin check via UserContext: ${user.email} -> ${isAdmin}`);
      }
      return isAdmin;
    }
    
    // Fallback to SecureTokenManager
    const tokenEmail = SecureTokenManager.getUserEmail();
    if (tokenEmail) {
      const isAdmin = tokenEmail === adminEmail;
      if (process.env.REACT_APP_DEBUG) {
        console.log(`Admin check via SecureTokenManager: ${tokenEmail} -> ${isAdmin}`);
      }
      return isAdmin;
    }
    
    // Fallback to sessionStorage
    try {
      const storedUser = sessionStorage.getItem('userInfo');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        const isAdmin = userData.email === adminEmail;
        if (process.env.REACT_APP_DEBUG) {
          console.log(`Admin check via sessionStorage: ${userData.email} -> ${isAdmin}`);
        }
        return isAdmin;
      }
    } catch (error) {
      // Failed to parse stored user data
    }
    
    if (process.env.REACT_APP_DEBUG) {
      console.log('Admin check: No user email found -> false');
    }
    return false;
  };

  const handleValidateAllStatuses = async () => {
    if (validating) return;
    
    const confirmed = window.confirm(
      'This will validate and auto-correct customer statuses and migrate completion dates for completed customers. This may take a few moments. Continue?'
    );
    
    if (!confirmed) return;
    
    setValidating(true);
    setValidationResult(null);
    
    try {
      // Run status validation first
      const statusResult = await googleSheets.validateAllCustomerStatuses();
      
      // Run completion date migration
      const migrationResult = await googleSheets.migrateCompletedCustomerDates();
      
      const message = `Fix complete: ${statusResult.totalCorrected} status corrections, ${migrationResult.totalMigrated} completion dates migrated`;
      setValidationResult(message);
      
      if (statusResult.totalCorrected > 0) {
        console.log('Status corrections made:', statusResult.corrections);
      }
      if (migrationResult.totalMigrated > 0) {
        console.log('Completion date migrations:', migrationResult.migrations);
      }
      
      // Refresh dashboard after corrections
      if (statusResult.totalCorrected > 0 || migrationResult.totalMigrated > 0) {
        setTimeout(() => onRefresh(), 1000);
      }
    } catch (error) {
      console.error('Fix operation failed:', error);
      setValidationResult('Fix operation failed. Check console for details.');
    } finally {
      setValidating(false);
      // Clear result message after 7 seconds (longer message needs more time)
      setTimeout(() => setValidationResult(null), 7000);
    }
  };

  // Defensive check for admin status to prevent breaking dashboard
  const showAdminButton = (() => {
    try {
      return isAdminUser();
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false; // Default to not showing admin button on error
    }
  })();

  return (
    <div className="dashboard-header">
      <h1>Customer Onboarding Dashboard</h1>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {showAdminButton && (
          <button 
            onClick={handleValidateAllStatuses}
            className="refresh-button"
            style={{ backgroundColor: validating ? '#ccc' : '#f39c12' }}
            disabled={validating}
            title="Validate and fix customer statuses and migrate completion dates for completed customers"
            type="button"
          >
            {validating ? 'Fixing...' : 'Fix Status & Migration Issues'}
          </button>
        )}
        <button 
          onClick={onRefresh} 
          className="refresh-button"
          aria-label="Refresh dashboard data"
          type="button"
        >
          Refresh
        </button>
        {validationResult && (
          <span 
            style={{ 
              fontSize: '0.9rem', 
              color: validationResult.includes('failed') ? '#e74c3c' : '#27ae60',
              fontWeight: 'bold'
            }}
          >
            {validationResult}
          </span>
        )}
      </div>
    </div>
  );
};

export default DashboardHeader;